/**
 * Authoritative customer-facing proposal access state.
 * All Link B / wrapper / PDF / Cleo public gates must use this helper.
 *
 * Precedence:
 * 1. Never public (draft / not sent) → NOT_PUBLIC
 * 2. Cancelled → CANCELLED_UNACCEPTED (customer content revoked for the whole family,
 *    including previously accepted offers; accepted PDFs remain staff-internal only)
 * 3. Accepted (approved / Won projected|completed / approvedAt) → PERMANENT_ACCEPTED_RECORD
 *    (Archive and expiry do NOT remove accepted access)
 * 4. Else archived → ARCHIVED_INVALID
 * 5. Else expired (Link B window) → EXPIRED
 * 6. Else sent/approved-offer → ACTIVE_OFFER
 */

import { isLinkBPastExpiry } from "./proposalLinkBExpiry";
import { isProposalArchived } from "./proposalArchive";

export type ProposalPublicAccessState =
  | "ACTIVE_OFFER"
  | "PERMANENT_ACCEPTED_RECORD"
  | "ARCHIVED_INVALID"
  | "EXPIRED"
  | "CANCELLED_UNACCEPTED"
  | "NOT_PUBLIC";

export type ProposalPublicAccessInput = {
  status?: string | null;
  pipelineStage?: string | null;
  approvedAt?: Date | string | null;
  cancelledAt?: Date | string | null;
  archivedAt?: Date | string | null;
  sentAt?: Date | string | null;
  linkBExpiresAt?: Date | string | null;
};

export type ProposalPublicAccessResult = {
  state: ProposalPublicAccessState;
  /** True when customer may view commercial content (offer or accepted record). */
  canViewContent: boolean;
  /** Accepted commercial record (customer-approved or staff Won). */
  isAcceptedRecord: boolean;
  /** Show cancelled/project-closed banner on permanent accepted page. */
  showCancelledBanner: boolean;
  /** Soft-revoked offer (archive) — dedicated invalid page. */
  showArchivedInvalidPage: boolean;
  showExpiredPage: boolean;
  showCancelledUnacceptedPage: boolean;
};

export function isProposalAcceptedCommercialRecord(row: ProposalPublicAccessInput): boolean {
  if (row.approvedAt != null && String(row.approvedAt).trim() !== "") return true;
  const status = String(row.status ?? "").toLowerCase();
  if (status === "approved") return true;
  const stage = String(row.pipelineStage ?? "").toLowerCase();
  return stage === "projected" || stage === "completed";
}

function isCancelled(row: ProposalPublicAccessInput): boolean {
  return row.cancelledAt != null && String(row.cancelledAt).trim() !== "";
}

function isPublicOfferStatus(row: ProposalPublicAccessInput): boolean {
  const status = String(row.status ?? "").toLowerCase();
  return status === "sent" || status === "approved";
}

/**
 * Central public-access resolver. Do not scatter archive/expiry/cancel checks
 * across routes — call this and branch on `state`.
 */
export function getProposalPublicAccessState(row: ProposalPublicAccessInput): ProposalPublicAccessResult {
  const accepted = isProposalAcceptedCommercialRecord(row);
  const cancelled = isCancelled(row);
  const archived = isProposalArchived(row);

  // Authoritative Cancel terminates all customer-facing Link B / PDF / OTP access,
  // including previously accepted offers. Historical accepted evidence stays
  // available to staff internally — not via the public customer gate.
  if (cancelled) {
    return {
      state: "CANCELLED_UNACCEPTED",
      canViewContent: false,
      isAcceptedRecord: accepted,
      showCancelledBanner: false,
      showArchivedInvalidPage: false,
      showExpiredPage: false,
      showCancelledUnacceptedPage: true,
    };
  }

  if (accepted) {
    return {
      state: "PERMANENT_ACCEPTED_RECORD",
      canViewContent: true,
      isAcceptedRecord: true,
      showCancelledBanner: false,
      showArchivedInvalidPage: false,
      showExpiredPage: false,
      showCancelledUnacceptedPage: false,
    };
  }

  if (archived) {
    return {
      state: "ARCHIVED_INVALID",
      canViewContent: false,
      isAcceptedRecord: false,
      showCancelledBanner: false,
      showArchivedInvalidPage: true,
      showExpiredPage: false,
      showCancelledUnacceptedPage: false,
    };
  }

  if (isLinkBPastExpiry(row) && !accepted) {
    // Only unaccepted offers expire
    if (isPublicOfferStatus(row) || row.sentAt) {
      return {
        state: "EXPIRED",
        canViewContent: false,
        isAcceptedRecord: false,
        showCancelledBanner: false,
        showArchivedInvalidPage: false,
        showExpiredPage: true,
        showCancelledUnacceptedPage: false,
      };
    }
  }

  if (isPublicOfferStatus(row)) {
    return {
      state: "ACTIVE_OFFER",
      canViewContent: true,
      isAcceptedRecord: false,
      showCancelledBanner: false,
      showArchivedInvalidPage: false,
      showExpiredPage: false,
      showCancelledUnacceptedPage: false,
    };
  }

  return {
    state: "NOT_PUBLIC",
    canViewContent: false,
    isAcceptedRecord: false,
    showCancelledBanner: false,
    showArchivedInvalidPage: false,
    showExpiredPage: false,
    showCancelledUnacceptedPage: false,
  };
}
